import React, { useState, useEffect, useRef } from "react";
import { SERVER_URL } from "../api";

const REGISTRATION_PAGE_ID = "registration";

function resolveUrl(url) {
	if (!url || !String(url).trim()) return null;
	if (url.startsWith("http") || url.startsWith("/") || url.startsWith("data:")) return url;
	return `${SERVER_URL}/${url}`;
}

const isShapeGradient = (v) => v && String(v).trim().startsWith("linear-gradient");

export function EscapeRoomElement({
	element,
	quizData,
	answer,
	registrationDesign,
	onButtonAction,
	onQuizOptionSelect,
	onHintUsed,
}) {
	const editRef = useRef(null);
	const [hintModal, setHintModal] = useState(null);

	useEffect(() => {
		if (element.type === "text" && editRef.current && element.content) {
			const html = element.content.html || `<p>${element.content.text || "Texto"}</p>`;
			if (editRef.current.innerHTML !== html) {
				editRef.current.innerHTML = html;
			}
		}
	}, [element.elementId, element.type, element.content?.html, element.content?.text]);

	const style = {
		position: "absolute",
		left: element.x,
		top: element.y,
		width: element.width,
		height: element.height,
		overflow: "hidden",
		boxSizing: "border-box",
		border: "none",
		outline: "none",
	};

	// Text (misma estructura y estilos que backoffice: fit-content, inline-block)
	if (element.type === "text") {
		const s = element.style || {};
		const fontFamily = s.fontFamily || "Arial, sans-serif";
		const fontSize = s.fontSize ?? 18;
		const textContentStyle = {
			padding: 8,
			fontFamily,
			fontSize: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
			color: s.color || "#000",
			backgroundColor: s.backgroundColor === "transparent" ? "transparent" : (s.backgroundColor || "transparent"),
			fontWeight: s.fontWeight || "normal",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			textAlign: s.textAlign || "left",
			display: "inline-block",
			width: "fit-content",
			minWidth: 20,
			minHeight: 24,
			outline: "none",
			boxSizing: "border-box",
			opacity: s.opacity != null ? s.opacity : 1,
			...(s.lineHeight != null && { lineHeight: s.lineHeight }),
			...(s.letterSpacing != null && { letterSpacing: s.letterSpacing }),
		};
		return (
			<div
				style={{
					...style,
					width: "fit-content",
					height: "fit-content",
					["--er-font"]: fontFamily,
					["--er-size"]: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
				}}
			>
				<div ref={editRef} className="escape-room-text-content" style={textContentStyle} />
			</div>
		);
	}

	// Image
	if (element.type === "image") {
		const isLogo = element.registrationRole === "logo" || element.registrationRole === "logoOncommit" || element.registrationRole === "logoJandJ";
		const imgUrl = isLogo && registrationDesign?.logoUrl ? registrationDesign.logoUrl : element.content?.url;
		const resolved = resolveUrl(imgUrl);
		return (
			<div style={style}>
				{resolved ? (
					<img
						src={resolved}
						alt={element.content?.alt || ""}
						style={{ width: "100%", height: "100%", objectFit: isLogo ? "contain" : "cover", opacity: element.style?.opacity ?? 1 }}
					/>
				) : (
					<div style={{ padding: 8, color: "#999", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>Imagen</div>
				)}
			</div>
		);
	}

	// Video
	if (element.type === "video") {
		const resolved = resolveUrl(element.content?.url);
		return (
			<div style={style}>
				{resolved ? (
					<video
						src={resolved}
						controls
						preload="metadata"
						playsInline
						autoPlay={!!element.content?.autoplay}
						muted={!!element.content?.autoplay}
						style={{ width: "100%", height: "100%", objectFit: "contain" }}
					/>
				) : (
					<div style={{ padding: 8, color: "#999", fontSize: 12 }}>Video</div>
				)}
			</div>
		);
	}

	// Shape (alineado con backoffice: fillColor, strokeColor, shapeType)
	if (element.type === "shape") {
		const c = element.content || {};
		const fill = c.fillColor || c.fill || "rgba(100, 100, 255, 0.8)";
		const stroke = c.strokeColor || c.stroke || "transparent";
		const strokeW = c.strokeWidth ?? 2;
		const shapeType = c.shapeType || "square";
		const radius = c.borderRadius ?? 0;
		const opacity = element.style?.opacity ?? 1;
		const fillIsGrad = isShapeGradient(fill);
		const strokeIsGrad = isShapeGradient(stroke);
		const fillBg = fillIsGrad ? { background: fill } : { backgroundColor: fill };
		const strokeBorder = strokeIsGrad ? {} : { border: `${strokeW}px solid ${stroke}` };

		let innerStyle = {
			width: "100%",
			height: "100%",
			opacity,
			boxSizing: "border-box",
			...fillBg,
			...strokeBorder,
		};

		if (shapeType === "circle") {
			innerStyle = { ...innerStyle, borderRadius: "50%" };
		} else if (shapeType === "line") {
			innerStyle = {
				width: "100%",
				height: Math.max(strokeW, 2),
				minHeight: Math.max(strokeW, 2),
				opacity,
				...(strokeIsGrad ? { background: stroke } : { backgroundColor: stroke }),
				border: "none",
				alignSelf: "center",
				borderRadius: radius ? `${Math.min(radius, 999)}px` : 0,
			};
		} else if (shapeType === "triangle") {
			// Triángulo via SVG
			return (
				<div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible", opacity }}>
						<polygon
							points="50,5 95,95 5,95"
							fill={fillIsGrad ? fill : fill}
							stroke={!strokeIsGrad && stroke !== "transparent" ? stroke : "none"}
							strokeWidth={strokeW}
						/>
					</svg>
				</div>
			);
		} else {
			innerStyle.borderRadius = radius ? `${Math.min(radius, 999)}px` : 0;
		}

		const needsStrokeWrapper = strokeIsGrad && strokeW > 0 && shapeType !== "line";
		const innerRadius = shapeType === "circle" ? "50%" : radius ? `${Math.min(radius, 999)}px` : 0;
		const wrapperStyle = needsStrokeWrapper
			? {
					width: "100%",
					height: "100%",
					padding: strokeW,
					background: stroke,
					borderRadius: shapeType === "circle" ? "50%" : radius + strokeW,
					boxSizing: "border-box",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					opacity,
			  }
			: null;
		const wrappedInnerStyle = needsStrokeWrapper
			? { flex: 1, minWidth: 0, minHeight: 0, ...fillBg, borderRadius: innerRadius, boxSizing: "border-box" }
			: null;

		return (
			<div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center" }}>
				{needsStrokeWrapper ? (
					<div style={wrapperStyle}>
						<div style={wrappedInnerStyle} />
					</div>
				) : (
					<div style={innerStyle} />
				)}
			</div>
		);
	}

	// Hint (fuente mediante variable CSS para anular body !important)
	if (element.type === "hint") {
		const c = element.content || {};
		const label = c.label || "Pista";
		const hintText = c.hintText || "";
		const hintPenalty = c.hintPenalty ?? 0;
		const s = element.style || {};
		const hintSize = s.fontSize ?? 14;
		const btnStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: s.backgroundColor || "#f5f3ff",
			color: s.color || "#7c3aed",
			fontSize: typeof hintSize === "number" ? `${hintSize}px` : hintSize,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: (s.borderRadius ?? 8) >= 0 ? s.borderRadius : 8,
			border: "1px solid #7c3aed",
			cursor: "pointer",
		};
		const handleHintClick = (e) => {
			e.stopPropagation();
			if (!hintText.trim()) return;
			if (hintPenalty > 0) {
				setHintModal({ type: "confirm", penalty: hintPenalty, text: hintText, label });
			} else {
				setHintModal({ type: "hint", text: hintText });
			}
		};
		const handleConfirmAccept = () => {
			setHintModal({ type: "hint", text: hintModal.text });
			if (onHintUsed && hintPenalty > 0) onHintUsed(element.elementId, hintPenalty);
		};
		return (
			<>
				<div style={{ ...style, ["--er-hint-font"]: s.fontFamily || "Arial, sans-serif", ["--er-hint-size"]: typeof hintSize === "number" ? `${hintSize}px` : hintSize }} onClick={handleHintClick}>
					<div className="escape-room-hint-content" style={btnStyle}>💡 {label}</div>
				</div>
				{hintModal?.type === "confirm" && hintModal.penalty === hintPenalty && (
					<div className="modal" style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
						<div style={{ background: "#fff", padding: 24, borderRadius: 8, maxWidth: 400 }}>
							<h3>Pista</h3>
							<p>Vas a perder {hintModal.penalty} puntos. ¿Estás seguro?</p>
							<div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
								<button onClick={() => setHintModal(null)}>Cancelar</button>
								<button onClick={handleConfirmAccept} className="btn-main">Aceptar</button>
							</div>
						</div>
					</div>
				)}
				{hintModal?.type === "hint" && (
					<div className="modal" style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
						<div style={{ background: "#fff", padding: 24, borderRadius: 8, maxWidth: 400, maxHeight: "80vh", overflow: "auto" }}>
							<h3>Pista</h3>
							<div style={{ whiteSpace: "pre-wrap" }}>{hintModal.text}</div>
							<div style={{ marginTop: 16 }}>
								<button onClick={() => setHintModal(null)} className="btn-main">Cerrar</button>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	// Button (fit-content como en backoffice para que envuelva el contenido)
	if (element.type === "button") {
		const s = element.style || {};
		const label = element.content?.label || "";
		const icon = element.content?.icon || "";
		const bg = s.backgroundColor || "transparent";
		const hasBg = bg && bg !== "transparent";
		const radius = typeof s.borderRadius === "number" ? s.borderRadius : 8;
		const btnFontSize = s.fontSize ?? 16;
		const btnStyle = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 6,
			padding: "8px 16px",
			minWidth: 20,
			minHeight: 36,
			backgroundColor: bg,
			color: s.color || "rgba(51, 51, 51, 1)",
			fontSize: typeof btnFontSize === "number" ? `${btnFontSize}px` : btnFontSize,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			textAlign: s.textAlign || "center",
			borderRadius: radius >= 0 ? radius : 8,
			border: hasBg ? "none" : "1px dashed rgba(0,0,0,0.2)",
			boxShadow: hasBg ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
			cursor: "pointer",
			boxSizing: "border-box",
		};
		const handleClick = (e) => {
			e.stopPropagation();
			if (onButtonAction && element.content?.action) {
				onButtonAction(element);
			}
		};
		const iconPos = element.content?.iconPosition || "before";
		const showLabel = String(label).trim();
		const showIcon = String(icon).trim();
		const displayText = !showLabel && !showIcon ? "Clic aquí" : null;
		const iconSpan = showIcon ? <span style={{ fontSize: btnFontSize * 1.2 }}>{icon}</span> : null;
		const textSpan = (showLabel || displayText) ? <span>{showLabel || displayText}</span> : null;
		return (
			<div style={{ ...style, width: "fit-content", height: "fit-content", ["--er-btn-font"]: s.fontFamily || "Arial, sans-serif", ["--er-btn-size"]: typeof btnFontSize === "number" ? `${btnFontSize}px` : btnFontSize }} onClick={handleClick}>
				<div className="escape-room-button-content" style={btnStyle}>
					{iconPos === "before" ? iconSpan : textSpan}
					{iconPos === "before" ? textSpan : iconSpan}
				</div>
			</div>
		);
	}

	// Quiz (alineado con backoffice: layout, tamaños, estilos)
	if (element.type === "quiz") {
		const quiz = quizData;
		const questions = quiz?.questions || [];
		const firstQ = questions[0];
		const opts = firstQ?.options || [];
		const hasQuizContent = quiz && questions.length > 0;
		const answered = answer != null;
		const isImageType = firstQ?.type === "imageChoice" || firstQ?.type === "optionsWithImages";

		const qStyle = element.style || {};
		const optStyle = element.content?.quizOptionsStyle || {};
		const textStyle = {
			fontFamily: qStyle.fontFamily || "Arial, sans-serif",
			fontSize: qStyle.fontSize || 14,
			color: qStyle.color || "#1e293b",
			fontWeight: qStyle.fontWeight || "600",
			fontStyle: qStyle.fontStyle || "normal",
			textDecoration: qStyle.textDecoration || "none",
			textAlign: qStyle.textAlign || "left",
		};
		const optionTextStyle = {
			fontFamily: optStyle.fontFamily || "Arial, sans-serif",
			fontSize: optStyle.fontSize || 12,
			color: optStyle.color || "#334155",
			fontWeight: optStyle.fontWeight || "normal",
			fontStyle: optStyle.fontStyle || "normal",
			textDecoration: optStyle.textDecoration || "none",
			textAlign: optStyle.textAlign || "left",
		};

		if (!hasQuizContent) {
			return (
				<div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", borderRadius: 8 }}>
					<span style={{ color: "#94a3b8", fontSize: 12 }}>Quiz sin configurar</span>
				</div>
			);
		}

		const layout = firstQ?.optionsLayout || "row";
		const gridCols = Math.min(6, Math.max(1, firstQ?.gridColumns || 2));
		const handleOptionClick = !answered && onQuizOptionSelect ? (i) => onQuizOptionSelect(element.elementId, i, opts[i]?.correct) : undefined;

		let containerStyle = {};
		if (isImageType) {
			if (layout === "columns") {
				containerStyle = { flex: 1, display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%" };
			} else if (layout === "row") {
				containerStyle = { flex: 1, display: "flex", flexDirection: "column", flexWrap: "nowrap", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%" };
			} else {
				containerStyle = { flex: 1, display: "grid", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%", gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`, gridAutoRows: "minmax(0, 1fr)" };
			}
		} else {
			containerStyle = {
				flex: 1,
				display: layout === "grid" ? "grid" : "flex",
				flexDirection: layout === "columns" ? "row" : "column",
				flexWrap: "nowrap",
				gridTemplateColumns: layout === "grid" ? `repeat(${gridCols}, minmax(0, 1fr))` : undefined,
				gap: 6,
				minHeight: 0,
				minWidth: 0,
				overflow: "hidden",
			};
		}

		const qSize = qStyle.fontSize ?? 14;
		const optSize = optStyle.fontSize ?? 12;
		const quizVars = {
			["--er-q-font"]: (qStyle.fontFamily || "Arial, sans-serif"),
			["--er-q-size"]: typeof qSize === "number" ? `${qSize}px` : String(qSize || "14px"),
			["--er-opt-font"]: (optStyle.fontFamily || "Arial, sans-serif"),
			["--er-opt-size"]: typeof optSize === "number" ? `${optSize}px` : String(optSize || "12px"),
		};
		return (
			<div style={{ ...style, ...quizVars, display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" }}>
				<div style={{ flex: 1, overflow: "hidden", padding: isImageType ? 6 : 10, display: "flex", flexDirection: "column", gap: isImageType ? 0 : 8, minHeight: 0, boxSizing: "border-box" }}>
					{!isImageType && firstQ.text && (
						<div className="escape-room-quiz-question" style={{ ...textStyle, marginBottom: 4, flexShrink: 0 }}>{firstQ.text}</div>
					)}
					<div style={containerStyle}>
						{opts.map((opt, i) => {
							const isSelected = answer?.selectedIndex === i;
							const borderColor = answered ? (opt.correct ? "#22c55e" : isSelected ? "#ef4444" : "#e2e8f0") : "#e2e8f0";
							const bgColor = answered ? (opt.correct ? "#f0fdf4" : isSelected ? "#fef2f2" : "#fff") : "#fff";
							const optionLabel = (firstQ.type === "imageChoice" && !opt.text) ? `Opción ${i + 1}` : (opt.text || "Opción");
							const hasImg = opt.imageUrl && String(opt.imageUrl).trim();

							if (isImageType) {
								return (
									<div
										key={i}
										role="button"
										onClick={handleOptionClick ? () => handleOptionClick(i) : undefined}
										style={{
											display: "flex",
											flexDirection: "column",
											position: "relative",
											borderRadius: 8,
											background: bgColor,
											border: "2px solid",
											borderColor,
											overflow: "hidden",
											boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
											cursor: handleOptionClick ? "pointer" : "default",
											boxSizing: "border-box",
											flex: "1 1 0",
											minWidth: 0,
											minHeight: 0,
										}}
									>
										<div style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
											{hasImg ? (
												<img src={resolveUrl(opt.imageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
											) : (
												<span style={{ fontSize: 32, color: "#94a3b8" }}>🖼</span>
											)}
											{answered && (
												<div style={{ position: "absolute", top: 4, right: 4 }}>
													{opt.correct && <span style={{ color: "#22c55e", background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✓</span>}
													{isSelected && !opt.correct && <span style={{ color: "#ef4444", background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✗</span>}
												</div>
											)}
										</div>
										{firstQ.type === "optionsWithImages" && (
											<div className="escape-room-quiz-option" style={{ padding: "4px 6px", ...optionTextStyle, textAlign: "center", fontSize: (optionTextStyle.fontSize || 12) * 0.95, flexShrink: 0 }}>
												{optionLabel}
											</div>
										)}
									</div>
								);
							}

							return (
								<div
									key={i}
									className="escape-room-quiz-option"
									role="button"
									onClick={handleOptionClick ? () => handleOptionClick(i) : undefined}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 6,
										padding: "6px 8px",
										borderRadius: 6,
										background: bgColor,
										border: "1px solid",
										borderColor,
										...optionTextStyle,
										cursor: handleOptionClick ? "pointer" : "default",
										minHeight: 28,
										flex: 1,
										minWidth: 0,
										boxSizing: "border-box",
									}}
								>
									<span style={{ width: 20, flexShrink: 0, textAlign: "center", fontSize: 14 }}>
										{answered && opt.correct && <span style={{ color: "#22c55e" }}>✓</span>}
										{answered && isSelected && !opt.correct && <span style={{ color: "#ef4444" }}>✗</span>}
									</span>
									<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{optionLabel}</span>
								</div>
							);
						})}
					</div>
					{answered && firstQ.explanation && (
						<div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 4, padding: 6, background: "#f8fafc", borderRadius: 6, flexShrink: 0 }}>💡 {firstQ.explanation}</div>
					)}
					{answered && (
						<div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, flexShrink: 0 }}>
							{answer.correct ? `+${quiz?.pointsCorrect ?? 10} pts` : `${quiz?.pointsIncorrect ?? -5} pts`}
						</div>
					)}
				</div>
			</div>
		);
	}

	return null;
}

export function getPlayablePages(pages) {
	if (!pages || !Array.isArray(pages)) return [];
	return [...pages].filter((p) => (p.pageId || p._id) !== REGISTRATION_PAGE_ID).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}
